import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FilterEventData, StudyInstancesFilterSelectComponent } from '../study-instance-filter-select/study-instances-filter-select.component';
import { FieldmapInfo, FieldmapService } from '../service/fieldmap.service';
import { CropService } from '../../shared/crop/service/crop.service';
import { ProgramService } from '../../shared/program/service/program.service';
import { StudyService } from '../../shared/study/service/study.service';
import { FieldMapContext } from '../field-map-context.model';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil, throttleTime } from 'rxjs/operators';

interface GroupedFieldmap {
    header: boolean;
    programName?: string;
    studyName?: string;
    data?: FieldmapInfo;
}

@Component({
    selector: 'jhi-study-instances-view',
    templateUrl: './study-instances-view.component.html',
    styleUrls: ['./study-instances-view.component.css']
})
export class StudyInstancesViewComponent implements OnInit, AfterViewInit, OnDestroy {

    @Output()
        onAssign: EventEmitter<FieldmapInfo> = new EventEmitter<FieldmapInfo>();
    @Output()
        onUnAssign: EventEmitter<FieldmapInfo> = new EventEmitter<FieldmapInfo>();
    @Output()
        onLoadStudy: EventEmitter<FieldmapInfo> = new EventEmitter<FieldmapInfo>();
    @Output()
        onEditStudy: EventEmitter<FieldmapInfo> = new EventEmitter<FieldmapInfo>();

    @ViewChild('assignedFilter') assignedFilter: StudyInstancesFilterSelectComponent;
    @ViewChild('pendingFilter') pendingFilter: StudyInstancesFilterSelectComponent;

    @ViewChild('virtualScrollViewportAssigned') virtualScrollViewportAssigned!: CdkVirtualScrollViewport;
    assignedStudyInstancesSubject = new BehaviorSubject<FieldmapInfo[]>([]);
    assignedInstances$ = this.assignedStudyInstancesSubject.asObservable();
    loadingAssigned = false;
    allDataLoadedAssigned = false;
    pageSizeAssigned = 20;
    currentPageAssigned = 0;
    flattenedData: GroupedFieldmap[] = [];

    @ViewChild('virtualScrollViewportUnassigned') virtualScrollViewportUnassigned!: CdkVirtualScrollViewport;
    unassignedStudyInstancesSubject = new BehaviorSubject<FieldmapInfo[]>([]);
    unassignedInstances$ = this.unassignedStudyInstancesSubject.asObservable();
    loadingUnassigned = false;
    allDataLoadedUnassigned = false;
    pageSizeUnassigned = 20;
    currentPageUnassigned = 0;

    assignedFilterData: FilterEventData;
    pendingFilterData: FilterEventData;

    private destroy$ = new Subject<void>();

    constructor(private fieldmapService: FieldmapService,
                private cropService: CropService,
                private programService: ProgramService,
                private studyService: StudyService,
                private fieldmapContext: FieldMapContext) {
    }

    async ngOnInit(): Promise<void> {
    }

    ngAfterViewInit(): void {

        // Monitor scroll position changes
        this.virtualScrollViewportAssigned.scrolledIndexChange
            .pipe(
                throttleTime(200), // Prevent too many calls while scrolling
                distinctUntilChanged()
            )
            .subscribe((index) => {
                this.onScrollAssigned(index);
            });

        this.virtualScrollViewportUnassigned.scrolledIndexChange
            .pipe(
                throttleTime(200), // Prevent too many calls while scrolling
                distinctUntilChanged()
            )
            .subscribe((index) => {
                this.onScrollUnassigned(index);
            });
    }

    refreshAll() {
        this.refreshAssignedStudyInstances();
        this.refreshPendingStudyInstances();
    }

    refreshAssignedStudyInstances() {
        this.currentPageAssigned = 0;
        this.allDataLoadedAssigned = false;
        this.assignedStudyInstancesSubject.next([]);
        this.loadMoreAssigned();
    }

    refreshPendingStudyInstances() {
        this.currentPageUnassigned = 0;
        this.allDataLoadedUnassigned = false;
        this.unassignedStudyInstancesSubject.next([]);
        this.loadMoreUnassigned();
    }

    onAssignedFilterChanged(filterEventData: FilterEventData) {
        this.assignedFilterData = filterEventData;
        this.refreshAssignedStudyInstances();
    }

    onPendingFilterChanged(filterEventData: FilterEventData) {
        this.pendingFilterData = filterEventData;
        this.refreshPendingStudyInstances();
    }

    assignStudyToBlock(instance: FieldmapInfo, $event: MouseEvent) {
        $event.stopPropagation();
        $event.preventDefault();
        if (this.fieldmapContext.mouseEventListenerEnabled) {
            this.onAssign.emit(instance);
        }
    }

    unAssignStudyFromBlock(instance: FieldmapInfo, $event: MouseEvent) {
        $event.stopPropagation();
        $event.preventDefault();
        if (this.fieldmapContext.mouseEventListenerEnabled) {
            this.onUnAssign.emit(instance);
        }
    }

    loadStudy(instance: FieldmapInfo) {
        if (this.fieldmapContext.mouseEventListenerEnabled) {
            this.onLoadStudy.emit(instance);
        }
    }

    editStudy(instance: FieldmapInfo, $event: MouseEvent) {
        $event.stopPropagation();
        $event.preventDefault();
        if (this.fieldmapContext.mouseEventListenerEnabled) {
            this.onEditStudy.emit(instance);
        }

    }

    isStudySelected(instance: FieldmapInfo) {
        const study = this.fieldmapContext.studiesMap.get(instance.instanceId.toString());
        if (study) {
            return study.isSelected;
        } else {
            return false;
        }
    }

    isStudyVisible(instance: FieldmapInfo) {
        const study = this.fieldmapContext.studiesMap.get(instance.instanceId.toString());
        if (study) {
            return study.isVisible;
        } else {
            return false;
        }
    }

    hideStudy(instance: FieldmapInfo) {

    }

    trackByInstance(index: number, item: any): number {
        // Assuming instance has an id field - use the most unique identifier available
        return item.instanceId; // or another unique property like `${item.studyName}-${item.instanceNumber}`
    }

    clearFilterAssigned() {
        this.assignedFilter.clearFilter();
    }

    clearFilterUnAssigned() {
        this.pendingFilter.clearFilter();
    }

    toggeVisibility(instance: FieldmapInfo, visibility: 'SHOW' | 'HIDE', $event: MouseEvent) {
        $event.stopPropagation();
        $event.preventDefault();
        if (this.fieldmapContext.mouseEventListenerEnabled) {
            const study = this.fieldmapContext.studiesMap.get(instance.instanceId.toString());
            if (study && visibility === 'SHOW') {
                study.show();
            } else if (study && visibility === 'HIDE') {
                study.hide();
            }
        }
    }

    isButtonDisabled() {
        // Disable the button if the global mouse event listener is disabled.
        return !this.fieldmapContext.mouseEventListenerEnabled;
    }

    onScrollAssigned($event: number) {
        // Calculate if we need to load more data
        const end = this.virtualScrollViewportAssigned.getRenderedRange().end;
        const total = this.virtualScrollViewportAssigned.getDataLength();

        // Load more if we're near the end (last 5 items)
        if (end > total - 5 && !this.loadingAssigned && !this.allDataLoadedAssigned) {
            this.loadMoreAssigned();
        }
    }

    onScrollUnassigned($event: number) {
        // Calculate if we need to load more data
        const end = this.virtualScrollViewportUnassigned.getRenderedRange().end;
        const total = this.virtualScrollViewportUnassigned.getDataLength();

        // Load more if we're near the end (last 5 items)
        if (end > total - 5 && !this.loadingUnassigned && !this.allDataLoadedUnassigned) {
            this.loadMoreUnassigned();
        }
    }

    private loadMoreUnassigned() {

        if (this.loadingUnassigned || this.allDataLoadedUnassigned) {
            return;
        }

        if (this.pendingFilterData && this.pendingFilterData.cropName) {

            this.loadingUnassigned = true;

            this.fieldmapService.searchFieldMapInfo(this.pendingFilterData.cropName, {
                programUUID: this.pendingFilterData.programUUID,
                studyId: this.pendingFilterData.trialId,
                withGeoJsonOnly: false
            }, this.currentPageUnassigned, this.pageSizeUnassigned).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (response) => {

                    const newFieldMapInfoList = response.results;
                    const currentFieldMapInfoList = this.unassignedStudyInstancesSubject.value;

                    // Check if we've received less items than requested (meaning we're at the end)
                    if (newFieldMapInfoList.length < this.pageSizeUnassigned) {
                        this.allDataLoadedUnassigned = true;
                    }

                    // Append new locations to existing ones
                    this.unassignedStudyInstancesSubject.next([...currentFieldMapInfoList, ...newFieldMapInfoList]);

                    this.currentPageUnassigned++;
                    this.loadingUnassigned = false;
                },
                error: (error) => {
                    console.error('Error loading FieldMapInfo:', error);
                    this.loadingUnassigned = false;
                }
            });
        }
    }

    private loadMoreAssigned() {

        if (this.loadingAssigned || this.allDataLoadedAssigned) {
            return;
        }

        if (this.assignedFilterData && this.assignedFilterData.cropName) {

            this.loadingAssigned = true;

            this.fieldmapService.searchFieldMapInfo(this.assignedFilterData.cropName, {
                programUUID: this.assignedFilterData.programUUID,
                studyId: this.assignedFilterData.trialId,
                withGeoJsonOnly: true
            }, this.currentPageAssigned, this.pageSizeAssigned).pipe(
                takeUntil(this.destroy$)
            ).subscribe({
                next: (response) => {

                    const newFieldMapInfoList = response.results;
                    const currentFieldMapInfoList = this.assignedStudyInstancesSubject.value;

                    // Check if we've received less items than requested (meaning we're at the end)
                    if (newFieldMapInfoList.length < this.pageSizeUnassigned) {
                        this.allDataLoadedAssigned = true;
                    }

                    // Append new locations to existing ones
                    const results = [...currentFieldMapInfoList, ...newFieldMapInfoList];
                    this.assignedStudyInstancesSubject.next(results);

                    const groupedFieldMaps = this.groupFieldmaps(results);
                    this.flattenedData = this.flattenForVirtualScroll(groupedFieldMaps);

                    this.currentPageAssigned++;
                    this.loadingAssigned = false;
                },
                error: (error) => {
                    console.error('Error loading FieldMapInfo:', error);
                    this.loadingAssigned = false;
                }
            });
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private groupFieldmaps(data: FieldmapInfo[]) {
        return data.reduce((acc, item) => {
            const key = `${item.programName}-${item.studyName}`;
            if (!acc[key]) {
                acc[key] = {
                    programName: item.programName,
                    studyName: item.studyName,
                    instances: []
                };
            }
            acc[key].instances.push(item);
            return acc;
        }, {} as Record<string, { programName: string; studyName: string; instances: FieldmapInfo[] }>);
    }

    private flattenForVirtualScroll(groupedData: Record<string, {
        programName: string;
        studyName: string;
        instances: FieldmapInfo[]
    }>) {
        const flattened: GroupedFieldmap[] = [];

        Object.values(groupedData).forEach((group) => {
            // Add header row
            flattened.push({
                header: true,
                programName: group.programName,
                studyName: group.studyName
            });

            // Add data rows
            group.instances.forEach((instance) => {
                flattened.push({
                    header: false,
                    data: instance
                });
            });
        });

        return flattened;
    }
}
